/**
 * Browser-side image resize before upload.
 *
 * resizeImage(file, opts)
 *   opts.maxSide     — longest side cap (default 800)
 *   opts.quality     — 0-1 output quality for lossy formats (default 0.85)
 *   opts.forceFormat — force output MIME ("image/jpeg" | "image/webp" | null). null = auto.
 *
 * Auto format: PNG input → PNG output (alpha preserved). Others → WebP.
 * ALWAYS re-encodes via canvas (never returns the raw File) to guarantee
 * consistent Blob type for FormData uploads across all browsers.
 * Returns { blob, width, height, mimeType }.
 */
export async function resizeImage(file, opts = {}) {
  // Support legacy (file, maxPx) signature
  if (typeof opts === 'number') opts = { maxSide: opts };
  const { maxSide = 800, quality = 0.85, forceFormat = null } = opts;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxSide || height > maxSide) {
        const scale = maxSide / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      // Determine output MIME
      let outMime;
      if (forceFormat) {
        outMime = forceFormat;
      } else {
        outMime = file.type === 'image/png' ? 'image/png' : 'image/webp';
      }

      // Preserve transparency for PNG output
      if (outMime === 'image/png') {
        ctx.clearRect(0, 0, width, height);
      }
      ctx.drawImage(img, 0, 0, width, height);

      // PNG is lossless (no quality arg). JPEG/WebP use quality.
      const q = outMime === 'image/png' ? undefined : quality;
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Canvas toBlob failed')); return; }
          resolve({ blob, width, height, mimeType: outMime });
        },
        outMime,
        q,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
    img.src = url;
  });
}
